import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { ControlledCheckbox } from '~/components/atoms/checkbox/Checkbox';
import XMarkIcon from '~/components/atoms/icon/xMark/XMarkIcon';
import {
    KeyWordFilter_ControlsState,
    KeyWordFilter_State,
    KeyWordFilterKey_ControlsState,
    KeyWordFilterKey_State,
} from '~/core/features/filtering/types/keywordFilter.type';
import { UpdateFilterControls } from '~/core/useSearchState';
import sanitizeString from '~/utils/sanitizeString';
import { CheckboxFilterLabel } from './checkboxFilterLabel/CheckboxFilterLabel';
import CheckboxFilterWrapper from './checkboxFilterWrapper/checkboxFilterWrapper';
import filterStyles from './filter.module.scss';
import { compareStringOrNumberKeys } from './filterUtils';

type KeywordWithControls = {
    keywordState: KeyWordFilterKey_State;
    keywordControls: KeyWordFilterKey_ControlsState;
};

type KeyWordFilterProps = {
    filterState: KeyWordFilter_State;
    filterControls: KeyWordFilter_ControlsState;
    updateFilterControls: UpdateFilterControls;
    resetFilterControls: () => void;
    showAllValues: boolean;
    setShowMoreButton: Dispatch<SetStateAction<boolean>>;
    scrollRef: React.RefObject<HTMLFieldSetElement>;
};

export const KeyWordFilter = ({
    filterState,
    filterControls,
    updateFilterControls,
    resetFilterControls,
    showAllValues,
    setShowMoreButton,
    scrollRef,
}: KeyWordFilterProps) => {
    const intl = useIntl();
    const [visibleKeywords, setVisibleKeywords] = useState(filterState.values.keywords);
    const [filterSearchInput, setFilterSearchInput] = useState('');
    const serpInputRef = useRef<HTMLInputElement>(null);

    const keywordsToRender = useMemo(() => {
        return visibleKeywords
            .reduce((acc, keyword) => {
                if (keyword.key.toString().length === 0) {
                    return acc;
                }

                const keywordControls: KeyWordFilterKey_ControlsState | undefined = filterControls.values.keywords.find(
                    (kw) => kw.key === keyword.key
                );

                if (!keywordControls) {
                    throw new Error(
                        `keywordControls not found for ${keyword.key} in ${filterState.name} - ${filterState.identifier}`
                    );
                }

                return [
                    ...acc,
                    {
                        keywordState: keyword,
                        keywordControls: keywordControls,
                    },
                ];
            }, [] as Array<KeywordWithControls>)
            .sort((a, b) => {
                const aChecked = a.keywordControls.isChecked;
                const bChecked = b.keywordControls.isChecked;
                const aState = a.keywordState;
                const bState = b.keywordState;
                if (aChecked) {
                    if (bChecked) {
                        return compareStringOrNumberKeys(aState.key, bState.key);
                    }
                    return -1;
                }
                if (bChecked) {
                    return 1;
                }
                return 0;
            })
            .slice(0, showAllValues ? filterState.values.keywords.length : filterState.configuration.maxValues);
    }, [
        visibleKeywords,
        filterControls,
        showAllValues,
        filterState.values.keywords.length,
        filterState.configuration.maxValues,
        filterState.identifier,
        filterState.name,
    ]);

    // when searching the filter, show only keywords relevant to search
    const setVisibleKeywordsAfterSearch = (inputValue: string) => {
        const sanitizedInputValue = sanitizeString(inputValue);
        // set visible keywords to only those, which either include the search term,
        // or which are included in the search term
        setVisibleKeywords(
            filterState.values.keywords.filter(
                (keyword) =>
                    (sanitizedInputValue.includes(sanitizeString(keyword.key.toString())) ||
                        sanitizeString(keyword.key.toString()).includes(sanitizedInputValue)) &&
                    keyword.key !== ''
            )
        );
    };

    const rowVirtualizer = useVirtualizer({
        count: keywordsToRender.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 31,
        overscan: 5,
        gap: 6,
        measureElement: (element: HTMLElement) => element.offsetHeight,
    });

    useEffect(() => {
        rowVirtualizer.measure();
    }, [showAllValues, rowVirtualizer]);

    useEffect(() => {
        if (filterSearchInput.length > 0) {
            setVisibleKeywordsAfterSearch(filterSearchInput);
        } else {
            setVisibleKeywords(filterState.values.keywords);
        }

        setShowMoreButton(filterSearchInput === '');
    }, [filterSearchInput, filterState.values.keywords]);

    const selectedCount = useMemo(() => {
        return 'keywords' in filterControls.values
            ? filterControls.values.keywords.filter((keyword) => keyword.isChecked).length
            : 0;
    }, [filterControls.values]);

    // TODO: Consider creating atom element along the idea of "AutoUnfocusInputField"
    // which infocuses on pressing of enter
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'Enter':
                serpInputRef.current?.blur();
                break;
        }
    };

    return (
        <div>
            <div className={classNames(filterStyles.filterActions)}>
                {filterState.configuration.hasSearchInput && (
                    <div className={classNames(filterStyles.filterSearch)}>
                        <input
                            ref={serpInputRef}
                            className={classNames(filterStyles.filterSearchInput)}
                            placeholder={`${intl.formatMessage({
                                id: 'search_in_filter_placeholder',
                                defaultMessage: 'Hledejte v',
                            })} „${filterState.name}“`}
                            value={filterSearchInput}
                            onChange={(e) => setFilterSearchInput(e.target.value)}
                            maxLength={30}
                            onKeyDown={handleKeyDown}
                            enterKeyHint="done"
                        ></input>
                        {filterSearchInput.length > 0 && (
                            <div role="button" className={classNames(filterStyles.filterClearSelected)}>
                                <XMarkIcon onClick={() => setFilterSearchInput('')} width={16} height={16} />
                            </div>
                        )}
                    </div>
                )}
                {selectedCount >= 3 && (
                    <div
                        className={classNames(filterStyles.filterClearSelected)}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            resetFilterControls();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                resetFilterControls();
                            }
                        }}
                    >
                        <XMarkIcon width={16} height={16} />
                        <FormattedMessage id="clear_selected" defaultMessage="Zrušit výběr" />
                    </div>
                )}
            </div>
            {visibleKeywords.length === 0 && filterState.configuration.hasSearchInput && (
                <span className={filterStyles.filterSearchNoResults}>
                    <FormattedMessage
                        id="search_in_filter_no_results"
                        defaultMessage={`Hledaný výraz "{query}" nenalezen.`}
                        values={{ query: filterSearchInput }}
                    />
                </span>
            )}
            <div
                style={{
                    height: `${rowVirtualizer.getTotalSize() + 6}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                    const value = keywordsToRender[virtualItem.index];
                    const { keywordState: keyword, keywordControls: controls } = value;

                    const checkedWithoutResults = controls.isChecked && keyword.doc_count === 0;

                    return (
                        <CheckboxFilterWrapper
                            data-index={virtualItem.index}
                            key={virtualItem.key as React.Key}
                            data-testid="filters-filter-checkbox"
                            ref={rowVirtualizer.measureElement}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualItem.start}px)`,
                            }}
                        >
                            <ControlledCheckbox
                                labelElement={
                                    <KeyWordLabel
                                        filterState={filterState}
                                        filterControls={filterControls}
                                        keyWordControl={controls}
                                        keyWordState={keyword}
                                    />
                                }
                                labelAttributes={{
                                    className: checkedWithoutResults ? filterStyles.checkedWithoutResults : '',
                                    'data-testid': 'filters-filter-checkbox-label',
                                }}
                                name={`filter-${filterState.identifier}-${keyword.key}`}
                                id={`filter-${filterState.identifier}-${keyword.key}`}
                                checked={controls.isChecked}
                                handleChange={(e) => {
                                    updateFilterControls({
                                        value: {
                                            key: keyword.key,
                                            check: e.target.checked,
                                        },
                                    });
                                }}
                                disabled={keyword.isDisabled}
                                data-testid="filters-filter-checkbox-input"
                            />
                        </CheckboxFilterWrapper>
                    );
                })}
            </div>
        </div>
    );
};

const KeyWordLabel = ({
    filterState,
    filterControls,
    keyWordControl,
    keyWordState,
}: {
    filterState: KeyWordFilter_State;
    filterControls: KeyWordFilter_ControlsState;
    keyWordControl: KeyWordFilterKey_ControlsState;
    keyWordState: KeyWordFilterKey_State;
}) => {
    const description = `${keyWordState.key} ${filterState.configuration.unit ?? ''}`;
    const showPlus =
        filterState.configuration.valuesLogic === 'OR' && filterControls.applyFilter && keyWordState.doc_count > 0;
    const aggregationText = `${showPlus ? '+' : ' '}${keyWordState.doc_count}`;

    return (
        <CheckboxFilterLabel
            showAggregation={!keyWordControl.isChecked}
            description={description ?? ''}
            aggregationText={aggregationText}
        />
    );
};
